import type {
  CompleteDraftOrderRequest,
  Customization,
  DraftOrder,
  DraftOrderLine,
  PatchAdminV1StoresStoreIdDraftOrdersIdBody,
  PatchAdminV1StoresStoreIdDraftOrdersIdBodyLinesItem,
  PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody,
  PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody,
} from '@nucleus-commerce/api-client/admin';
import { money } from '@nucleus-commerce/core';

import type { CompleteFormInput, CompleteFormOutput } from '../schemas/complete-schema';
import type { CustomerFormInput, CustomerFormOutput } from '../schemas/customer-schema';
import {
  customizationValuesOf,
  toCustomizationInputs,
  type KeptFiles,
} from '../schemas/customization-rules';
import type { DiscountFormInput, DiscountFormOutput } from '../schemas/discount-schema';
import type { InvoiceFormInput, InvoiceFormOutput } from '../schemas/invoice-schema';
import type {
  CustomLineFormInput,
  CustomLineFormOutput,
  LineEditFormInput,
  LineEditFormOutput,
  ProductLineFormInput,
  ProductLineFormOutput,
} from '../schemas/line-schemas';
import type { NotesFormInput, NotesFormOutput } from '../schemas/notes-schema';
import type { ShippingFormInput, ShippingFormOutput } from '../schemas/shipping-schema';

export type DraftLineInput = PatchAdminV1StoresStoreIdDraftOrdersIdBodyLinesItem;
type Body = PatchAdminV1StoresStoreIdDraftOrdersIdBody;

const orNull = (value: string) => (value === '' ? null : value);

/**
 * A line as the backend needs it back. The backend replaces the whole list on every PATCH that
 * carries `lines`, so each change resends every line: customizations as the chosen raw values
 * (a file as its id, which keeps it), the unit price only when staff overrode it (null keeps the
 * store price moving with the catalog).
 */
export function toLineInput(line: DraftOrderLine): DraftLineInput | null {
  if (line.kind === 'custom') {
    return {
      kind: 'custom',
      title: line.title,
      sku: line.sku,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      requiresShipping: line.requiresShipping,
    };
  }
  if (!line.variantId) return null;
  return {
    kind: 'variant',
    variantId: line.variantId,
    quantity: line.quantity,
    customizations: line.customizations.map((c) => ({
      key: c.key,
      value: c.file ? c.file.id : c.value,
    })),
    unitPrice: line.priceOverridden ? line.unitPrice : null,
  };
}

export function linesInput(lines: readonly DraftOrderLine[]): DraftLineInput[] {
  return lines.flatMap((line) => {
    const input = toLineInput(line);
    return input ? [input] : [];
  });
}

export function withLineAdded(
  lines: readonly DraftOrderLine[],
  added: DraftLineInput,
): DraftLineInput[] {
  return [...linesInput(lines), added];
}

export function withoutLine(lines: readonly DraftOrderLine[], lineId: string): DraftLineInput[] {
  return linesInput(lines.filter((line) => line.id !== lineId));
}

export function emptyProductLineForm(): ProductLineFormInput {
  return {
    storeProductId: '',
    variantId: '',
    quantity: '1',
    overridePrice: false,
    unitPrice: null,
    customizations: {},
  };
}

export function toProductLineInput(
  values: ProductLineFormOutput,
  defs: readonly Customization[],
): DraftLineInput {
  return {
    kind: 'variant',
    variantId: values.variantId,
    quantity: Number(values.quantity),
    customizations: toCustomizationInputs(defs, values.customizations),
    unitPrice: values.overridePrice ? values.unitPrice : null,
  };
}

export function fromCatalogLine(
  line: DraftOrderLine,
  defs: readonly Customization[],
): LineEditFormInput {
  return {
    quantity: String(line.quantity),
    overridePrice: line.priceOverridden,
    unitPrice: line.priceOverridden ? line.unitPrice : null,
    customizations: customizationValuesOf(defs, line.customizations),
  };
}

type LineBody = PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody;

/** Quantity, price and the full set of answers; switching the override off sends null. */
export function toCatalogLineBody(
  values: LineEditFormOutput,
  defs: readonly Customization[],
  kept: KeptFiles,
): LineBody {
  return {
    quantity: Number(values.quantity),
    unitPrice: values.overridePrice ? values.unitPrice : null,
    customizations: toCustomizationInputs(defs, values.customizations, kept),
  };
}

export function emptyCustomLineForm(): CustomLineFormInput {
  return { title: '', sku: '', quantity: '1', unitPrice: null, requiresShipping: true };
}

export function fromCustomLine(line: DraftOrderLine): CustomLineFormInput {
  return {
    title: line.title,
    sku: line.sku ?? '',
    quantity: String(line.quantity),
    unitPrice: line.unitPrice,
    requiresShipping: line.requiresShipping,
  };
}

export function toCustomLineBody(values: CustomLineFormOutput): LineBody {
  return {
    title: values.title,
    sku: orNull(values.sku),
    quantity: Number(values.quantity),
    unitPrice: values.unitPrice,
    requiresShipping: values.requiresShipping,
  };
}

export function emptyInvoiceForm(draft: Pick<DraftOrder, 'email'>): InvoiceFormInput {
  return { to: draft.email ?? '', message: '' };
}

/** `to` only when it differs from the draft's own email; a blank message is left out. */
export function toInvoiceBody(
  values: InvoiceFormOutput,
  draft: Pick<DraftOrder, 'email'>,
): NonNullable<PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody> {
  return {
    ...(values.to && values.to !== draft.email ? { to: values.to } : {}),
    ...(values.message ? { message: values.message } : {}),
  };
}

export function toCustomLineInput(values: CustomLineFormOutput): DraftLineInput {
  return {
    kind: 'custom',
    title: values.title,
    sku: orNull(values.sku),
    quantity: Number(values.quantity),
    unitPrice: values.unitPrice,
    requiresShipping: values.requiresShipping,
  };
}

export function fromDraftDiscount(
  draft: Pick<DraftOrder, 'discount' | 'currency'>,
): DiscountFormInput {
  const d = draft.discount;
  return {
    type: d?.type ?? 'none',
    amount: d?.type === 'fixed' ? money(d.value, draft.currency) : null,
    percent: d?.type === 'percent' ? String(d.value) : '',
    reason: d?.reason ?? '',
  };
}

export function toDiscountBody(values: DiscountFormOutput): Pick<Body, 'discount'> {
  const reason = orNull(values.reason);
  if (values.type === 'fixed' && values.amount) {
    return { discount: { type: 'fixed', value: values.amount.amount, reason } };
  }
  if (values.type === 'percent') {
    return { discount: { type: 'percent', value: Number(values.percent), reason } };
  }
  return { discount: null };
}

export function fromDraftShipping(draft: Pick<DraftOrder, 'shipping'>): ShippingFormInput {
  const s = draft.shipping;
  if (s?.kind === 'rate') return { mode: 'rate', rateId: s.rateId ?? '', title: '', price: null };
  if (s?.kind === 'custom') return { mode: 'custom', rateId: '', title: s.title, price: s.price };
  return { mode: 'none', rateId: '', title: '', price: null };
}

export function toShippingBody(values: ShippingFormOutput): Pick<Body, 'shipping'> {
  if (values.mode === 'rate') return { shipping: { kind: 'rate', rateId: values.rateId } };
  if (values.mode === 'custom' && values.price) {
    return { shipping: { kind: 'custom', title: values.title, price: values.price } };
  }
  return { shipping: null };
}

export function fromDraftCustomer(
  draft: Pick<DraftOrder, 'customerId' | 'email' | 'phone'>,
): CustomerFormInput {
  return { customerId: draft.customerId, email: draft.email ?? '', phone: draft.phone ?? '' };
}

export function toCustomerBody(
  values: CustomerFormOutput,
): Pick<Body, 'customerId' | 'email' | 'phone'> {
  return {
    customerId: values.customerId,
    email: orNull(values.email),
    phone: orNull(values.phone),
  };
}

export function fromDraftNotes(
  draft: Pick<DraftOrder, 'note' | 'staffNotes' | 'tags'>,
): NotesFormInput {
  return { note: draft.note ?? '', staffNotes: draft.staffNotes ?? '', tags: [...draft.tags] };
}

export function toNotesBody(values: NotesFormOutput): Pick<Body, 'note' | 'staffNotes' | 'tags'> {
  return {
    note: orNull(values.note),
    staffNotes: orNull(values.staffNotes),
    tags: values.tags,
  };
}

export function emptyCompleteForm(): CompleteFormInput {
  return { type: 'paid', manualPaymentMethodId: '', reference: '', note: '' };
}

/** Optional keys are left out rather than sent empty. */
export function toCompleteBody(values: CompleteFormOutput): CompleteDraftOrderRequest {
  if (values.type === 'manual') {
    return { type: 'manual', manualPaymentMethodId: values.manualPaymentMethodId };
  }
  return {
    type: 'paid',
    ...(values.reference ? { reference: values.reference } : {}),
    ...(values.note ? { note: values.note } : {}),
  };
}
